import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileCheck, Award, Shield, BookOpen, ExternalLink } from 'lucide-react';

export default function ProofCenter() {
  const { t, language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);

  const tiles = {
    minedub: {
      title: 'Validation Ministérielle',
      subtitle: 'Cameroun - Ministère de l\'Éducation de Base',
      description: 'Approbation du Programme de Réarmement Patriotique par le Ministère de l\'Éducation de Base du Cameroun.',
      date: '01 Mars 2025',
      image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/n4gaq5o9_courrier%20du%20MINEDUB.png',
      icon: FileCheck,
      color: 'emerald'
    },
    inpi: {
      title: 'Propriété Intellectuelle INPI',
      subtitle: 'République Française',
      description: 'Modèle PICSU protégé et déposé auprès de l\'Institut National de la Propriété Industrielle.',
      number: 'DSO2026004704',
      image: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/127cz80b_INPI.png',
      icon: Shield,
      color: 'slate'
    },
    manuals: {
      title: 'Bibliothèque "Le Petit Patriote"',
      subtitle: 'Programme Bilingue FR/EN',
      description: 'Supports pédagogiques officiels pour l\'éducation civique bilingue. Collection complète CE1-CM2 (français) et Class 3-6 (anglais).',
      icon: BookOpen,
      color: 'sky',
      images: [
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/8zju4xcy_CE1.png', level: 'CE1' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/pff07ksk_CE2.png', level: 'CE2' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/fcmdlat5_CM1.png', level: 'CM1' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/ykzsrd4j_CM2.png', level: 'CM2' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/ca39tiy4_CLASS%203.png', level: 'Class 3' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/gliz04d0_CLASS%204.png', level: 'Class 4' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/ejdpvl3s_CLASS%205.png', level: 'Class 5' },
        { url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/p6veex5c_CLASS%206.png', level: 'Class 6' },
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white pt-32">
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="proof-center-hero">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-emerald-600/20 border border-emerald-400/30 rounded-full backdrop-blur-sm mb-6">
              <p className="text-sm font-medium text-emerald-300 uppercase tracking-wider">
                {language === 'fr' && 'Centre de Preuves & Accords'}
                {language === 'en' && 'Proof & Agreements Center'}
                {language === 'pt' && 'Centro de Provas & Acordos'}
                {language === 'it' && 'Centro Prove & Accordi'}
              </p>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
              {language === 'fr' && 'Nos Validations Officielles'}
              {language === 'en' && 'Our Official Validations'}
              {language === 'pt' && 'Nossas Validações Oficiais'}
              {language === 'it' && 'Le Nostre Validazioni Ufficiali'}
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              {language === 'fr' && 'Toutes les preuves de la légitimité institutionnelle de LENODA : validations gouvernementales, protection intellectuelle, et supports pédagogiques certifiés.'}
              {language === 'en' && 'All evidence of LENODA\'s institutional legitimacy: government validations, intellectual protection, and certified educational materials.'}
              {language === 'pt' && 'Todas as provas da legitimidade institucional da LENODA: validações governamentais, proteção intelectual e materiais educativos certificados.'}
              {language === 'it' && 'Tutte le prove della legittimità istituzionale di LENODA: validazioni governative, protezione intellettuale e materiali didattici certificati.'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="tiles-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div 
              className="group cursor-pointer bg-white border-2 border-emerald-200 rounded-3xl overflow-hidden hover:shadow-2xl transition-all"
              onClick={() => setSelectedImage(tiles.minedub.image)}
              data-testid="tile-minedub"
            >
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={tiles.minedub.image}
                  alt="Courrier MINEDUB" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="inline-block px-3 py-1 bg-emerald-600 rounded-full mb-3">
                      <p className="text-xs font-bold text-white">
                        {language === 'fr' && '01 Mars 2025'}
                        {language === 'en' && '01 March 2025'}
                        {language === 'pt' && '01 de Março de 2025'}
                        {language === 'it' && '01 Marzo 2025'}
                      </p>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {language === 'fr' && 'Validation Ministérielle'}
                      {language === 'en' && 'Ministerial Validation'}
                      {language === 'pt' && 'Validação Ministerial'}
                      {language === 'it' && 'Validazione Ministeriale'}
                    </h3>
                    <p className="text-sm text-slate-200">
                      {language === 'fr' && 'Cameroun - Ministère de l\'Éducation de Base'}
                      {language === 'en' && 'Cameroon - Ministry of Basic Education'}
                      {language === 'pt' && 'Camarões - Ministério da Educação Básica'}
                      {language === 'it' && 'Camerun - Ministero dell\'Istruzione di Base'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <FileCheck className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <p className="text-base text-slate-700">
                    {language === 'fr' && 'Approbation du Programme de Réarmement Patriotique par le Ministère de l\'Éducation de Base du Cameroun.'}
                    {language === 'en' && 'Approval of the Patriotic Rearmament Program by the Ministry of Basic Education of Cameroon.'}
                    {language === 'pt' && 'Aprovação do Programa de Rearmamento Patriótico pelo Ministério da Educação Básica dos Camarões.'}
                    {language === 'it' && 'Approvazione del Programma di Riarmo Patriottico da parte del Ministero dell\'Istruzione di Base del Camerun.'}
                  </p>
                </div>
                <p className="text-sm font-semibold text-emerald-600">
                  {language === 'fr' && 'Cliquer pour agrandir en HD →'}
                  {language === 'en' && 'Click to enlarge in HD →'}
                  {language === 'pt' && 'Clique para ampliar em HD →'}
                  {language === 'it' && 'Clicca per ingrandire in HD →'}
                </p>
              </div>
            </div>

            <div 
              className="group cursor-pointer bg-white border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-2xl transition-all"
              onClick={() => setSelectedImage(tiles.inpi.image)}
              data-testid="tile-inpi"
            >
              <div className="relative h-80 overflow-hidden bg-slate-50">
                <img 
                  src={tiles.inpi.image}
                  alt="Certificat INPI" 
                  className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Shield className="w-6 h-6 text-slate-900 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      {language === 'fr' && 'Propriété Intellectuelle INPI'}
                      {language === 'en' && 'INPI Intellectual Property'}
                      {language === 'pt' && 'Propriedade Intelectual INPI'}
                      {language === 'it' && 'Proprietà Intellettuale INPI'}
                    </h3>
                    <p className="text-sm font-medium text-slate-600 mb-3">
                      {language === 'fr' && 'République Française'}
                      {language === 'en' && 'French Republic'}
                      {language === 'pt' && 'República Francesa'}
                      {language === 'it' && 'Repubblica Francese'}
                    </p>
                    <p className="text-base text-slate-700 mb-3">
                      {language === 'fr' && 'Modèle PICSU protégé et déposé auprès de l\'Institut National de la Propriété Industrielle.'}
                      {language === 'en' && 'PICSU model protected and registered with the National Institute of Industrial Property.'}
                      {language === 'pt' && 'Modelo PICSU protegido e registrado no Instituto Nacional da Propriedade Industrial.'}
                      {language === 'it' && 'Modello PICSU protetto e depositato presso l\'Istituto Nazionale della Proprietà Industriale.'}
                    </p>
                    <div className="inline-block px-4 py-2 bg-slate-900 rounded-full">
                      <p className="text-sm font-mono font-bold text-white">
                        N° DSO2026004704
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm font-semibold text-slate-600 mt-4">
                  {language === 'fr' && 'Cliquer pour agrandir en HD →'}
                  {language === 'en' && 'Click to enlarge in HD →'}
                  {language === 'pt' && 'Clique para ampliar em HD →'}
                  {language === 'it' && 'Clicca per ingrandire in HD →'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-sky-50 to-white border-2 border-sky-200 rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-sky-600" />
              <div>
                <h2 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                  {language === 'fr' && 'Bibliothèque "Le Petit Patriote"'}
                  {language === 'en' && '"Le Petit Patriote" Library'}
                  {language === 'pt' && 'Biblioteca "Le Petit Patriote"'}
                  {language === 'it' && 'Biblioteca "Le Petit Patriote"'}
                </h2>
                <p className="text-sm font-medium text-sky-600">
                  {language === 'fr' && 'Programme Bilingue FR/EN'}
                  {language === 'en' && 'Bilingual Program FR/EN'}
                  {language === 'pt' && 'Programa Bilíngue FR/EN'}
                  {language === 'it' && 'Programma Bilingue FR/EN'}
                </p>
              </div>
            </div>
            <p className="text-base text-slate-700 mb-8">
              {language === 'fr' && 'Supports pédagogiques officiels pour l\'éducation civique bilingue. Collection complète CE1-CM2 (français) et Class 3-6 (anglais).'}
              {language === 'en' && 'Official educational materials for bilingual civic education. Complete collection CE1-CM2 (French) and Class 3-6 (English).'}
              {language === 'pt' && 'Materiais pedagógicos oficiais para educação cívica bilíngue. Coleção completa CE1-CM2 (francês) e Class 3-6 (inglês).'}
              {language === 'it' && 'Materiali didattici ufficiali per l\'educazione civica bilingue. Collezione completa CE1-CM2 (francese) e Class 3-6 (inglese).'}
            </p>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              {tiles.manuals.images.map((manual, i) => (
                <div 
                  key={i}
                  className="group cursor-pointer"
                  onClick={() => setSelectedImage(manual.url)}
                  data-testid={`manual-tile-${i}`}
                >
                  <div className="relative overflow-hidden rounded-xl border-2 border-sky-200 group-hover:border-sky-500 transition-all shadow-md">
                    <img 
                      src={manual.url}
                      alt={manual.level} 
                      className="w-full group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-end justify-center p-2">
                      <span className="text-white text-xs font-bold opacity-0 group-hover:opacity-100 bg-sky-600 px-2 py-1 rounded">
                        {manual.level}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center">
            <Award className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
              {language === 'fr' && 'Crédibilité Institutionnelle Totale'}
              {language === 'en' && 'Total Institutional Credibility'}
              {language === 'pt' && 'Credibilidade Institucional Total'}
              {language === 'it' && 'Credibilità Istituzionale Totale'}
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto mb-8">
              {language === 'fr' && 'LENODA dispose de l\'ensemble des validations nécessaires pour rassurer les investisseurs institutionnels et les gouvernements : approbation ministérielle, protection intellectuelle INPI, et supports pédagogiques certifiés.'}
              {language === 'en' && 'LENODA has all the necessary validations to reassure institutional investors and governments: ministerial approval, INPI intellectual protection, and certified educational materials.'}
              {language === 'pt' && 'LENODA possui todas as validações necessárias para tranquilizar investidores institucionais e governos: aprovação ministerial, proteção intelectual INPI e materiais educativos certificados.'}
              {language === 'it' && 'LENODA dispone di tutte le validazioni necessarie per rassicurare gli investitori istituzionali e i governi: approvazione ministeriale, protezione intellettuale INPI e materiali didattici certificati.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <div className="text-center">
                <div className="text-5xl font-bold text-emerald-600 mb-2">1</div>
                <p className="text-sm font-semibold text-slate-700">
                  {language === 'fr' && 'Validation Ministérielle'}
                  {language === 'en' && 'Ministerial Validation'}
                  {language === 'pt' && 'Validação Ministerial'}
                  {language === 'it' && 'Validazione Ministeriale'}
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-slate-900 mb-2">1</div>
                <p className="text-sm font-semibold text-slate-700">
                  {language === 'fr' && 'Protection INPI'}
                  {language === 'en' && 'INPI Protection'}
                  {language === 'pt' && 'Proteção INPI'}
                  {language === 'it' && 'Protezione INPI'}
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-sky-600 mb-2">8</div>
                <p className="text-sm font-semibold text-slate-700">
                  {language === 'fr' && 'Manuels Certifiés'}
                  {language === 'en' && 'Certified Manuals'}
                  {language === 'pt' && 'Manuais Certificados'}
                  {language === 'it' && 'Manuali Certificati'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="image-modal"
        >
          <button 
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <ExternalLink className="w-6 h-6 text-white rotate-45" />
          </button>
          <img 
            src={selectedImage}
            alt="Document agrandi" 
            className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
